import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';

// API configuration
const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

const BlockKAndLReport = () => {
  const navigate = useNavigate();
  
  // State for report data
  const [blockKData, setBlockKData] = useState([]);
  const [blockLData, setBlockLData] = useState([]);
  const [blockKItemData, setBlockKItemData] = useState({});
  const [blockLItemData, setBlockLItemData] = useState({});
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};
    
    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';

  // Memoized data transformation for Block K
  const transformBlockKData = useCallback((data) => {
    const initialData = {};
    data.forEach(item => {
      initialData[item.ictIndicatorCode] = {
        dslNo: item.dslNo,
        surveyYear: item.surveyYear,
        code: item.code,
        ictIndicatorCode: item.ictIndicatorCode,
        ItemsName: item.ItemsName,
        statusFlag: item.statusFlag
      };
    });
    return initialData;
  }, []);

  // Memoized data transformation for Block L
  const transformBlockLData = useCallback((data) => {
    const initialData = {};
    data.forEach(item => {
      // Use ecIndicatorCode as the key since that's what we have
      const indicatorCode = item.ecIndicatorCode;
      const itemName = item.ItemsName;
      const responseCode = item.code;
      
      console.log('Block L item:', { indicatorCode, itemName, responseCode });
      
      if (indicatorCode) {
        initialData[indicatorCode] = {
          dslNo: item.dslNo,
          surveyYear: item.surveyYear,
          code: responseCode,
          ecIndicatorCode: indicatorCode,
          ItemsName: itemName,
          statusFlag: item.statusFlag
        };
      }
    });
    return initialData;
  }, []);

  // Fetch Block K data
  const fetchBlockKData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year for Block K');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };
    
    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockKForm`, requestPayload, config);
      const { data, status } = response.data;
      
      if (status && data?.blockKData?.length > 0) {
        // Filter for ICT indicators only (ecIndicatorCode < 8)
        const ictData = data.blockKData.filter(item => 
          !item.ecIndicatorCode || item.ecIndicatorCode < 8
        );
        
        setBlockKData(ictData);
        const transformedData = transformBlockKData(ictData);
        setBlockKItemData(transformedData);
        console.log('Block K ICT data loaded successfully:', ictData.length, 'items');
      } else {
        setBlockKData([]);
        console.log('No Block K data found');
      }
    } catch (error) {
      console.error('Block K API Error:', error);
      setBlockKData([]);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockKData]);

  // Fetch Block L data (using same API as Block K but filtering for EC indicators)
  const fetchBlockLData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year for Block L');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };
    
    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockLForm`, requestPayload, config);
      console.log('Block L API Response:', response.data);
      const { data, status } = response.data;
      
      if (status && data) {
        // Get Block L data from proper API response
        let blockLDataArray = [];
        
        if (data.blockLData && Array.isArray(data.blockLData)) {
          // Use blockLData directly from Block L API
          blockLDataArray = data.blockLData;
          console.log('Block L data from blockLData array:', blockLDataArray);
        } else if (data.blockKData && Array.isArray(data.blockKData)) {
          // Fallback: Filter Block K data to get only EC indicators (ecIndicatorCode >= 8)
          blockLDataArray = data.blockKData.filter(item => 
            item.ecIndicatorCode && 
            (item.ecIndicatorCode === 8 || item.ecIndicatorCode === 9 || 
             item.ecIndicatorCode === 10 || item.ecIndicatorCode === 11)
          );
          console.log('Block L data from blockKData fallback:', blockLDataArray);
        } else {
          console.log('No blockLData or blockKData found in response');
        }
        
        console.log('Block L filtered data array:', blockLDataArray);
        console.log('Block L data length:', blockLDataArray.length);
        
        if (blockLDataArray.length > 0) {
          console.log('Setting Block L data...');
          setBlockLData(blockLDataArray);
          const transformedData = transformBlockLData(blockLDataArray);
          console.log('Block L transformed data:', transformedData);
          setBlockLItemData(transformedData);
          console.log('Block L data loaded successfully:', blockLDataArray.length, 'items');
        } else {
          console.log('No Block L data found - setting empty array');
          setBlockLData([]);
          console.log('No Block L data found in response');
        }
      } else {
        setBlockLData([]);
        console.log('Block L API returned no data or error status');
      }
    } catch (error) {
      console.error('Block L API Error:', error);
      setBlockLData([]);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockLData]);

  // Fetch both blocks data
  const fetchAllData = useCallback(async () => {
    showLoading(true);
    setLoading(true);
    
    try {
      await Promise.all([
        fetchBlockKData(),
        fetchBlockLData()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading report data', messages.COLOR_RED);
    } finally {
      showLoading(false);
      setLoading(false);
    }
  }, [fetchBlockKData, fetchBlockLData]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    return {
      slNo: 'SL No.',
      dslNo: 'DSL No.',
      surveyYear: 'Survey Year',
      blockKTitle: "Block 'K': Information and Communication Technology(ICT) USAGE",
      blockLTitle: "Block 'L': Energy Conservation(EC) Measures",
      ictIndicator: 'ICT Indicator',
      ecIndicator: 'EC Indicator',
      yesNo: 'Yes-1/No-2'
    };
  }, []);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      
      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
            line-height: 1.4;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
            font-size: 12px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 11px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .item-name {
            text-align: left;
            font-weight: 500;
            padding-left: 8px;
          }
          .blocks-container {
            display: flex;
            gap: 30px;
            margin-bottom: 20px;
          }
          .block-section {
            flex: 1;
            min-width: 0;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
        
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
            .blocks-container { flex-direction: column; gap: 20px; }
          }
          @media (max-width: 768px) {
            .blocks-container { flex-direction: column; gap: 20px; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <span style={{ color: '#ff6b35', fontWeight: 'bold' }}>Annual Survey of Industries, Uttar Pradesh</span>
            <span style={{ color: '#333', fontWeight: 'bold' }}> Block K & L</span>
          </div>
          
          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>
          
          <div className="blocks-container">
            {/* Block K Section */}
            <div className="block-section">
              <div className="report-title" style={{ fontSize: '16px', margin: '0 0 15px 0', padding: '10px 15px', borderBottom: 'none' }}>
                {tableHeaders.blockKTitle}
              </div>
              
              {(() => {
                console.log('Block K render check - blockKData:', blockKData);
                console.log('Block K render check - blockKData.length:', blockKData.length);
                console.log('Block K render check - blockKItemData:', blockKItemData);
                
                return blockKData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th style={{ width: '10%' }}>{tableHeaders.slNo}</th>
                          <th style={{ width: '70%' }}>{tableHeaders.ictIndicator}</th>
                          <th style={{ width: '20%' }}>{tableHeaders.yesNo}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blockKData.map((item, index) => {
                          console.log('Rendering Block K item:', item);
                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td className="item-name">{item.ItemsName}</td>
                              <td>{blockKItemData[item.ictIndicatorCode]?.code || ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">No Block K data available</div>
                );
              })()}
            </div>

            {/* Block L Section */}
            <div className="block-section">
              <div className="report-title" style={{ fontSize: '16px', margin: '0 0 15px 0', padding: '10px 15px', borderBottom: 'none' }}>
                {tableHeaders.blockLTitle}
              </div>
              
              {(() => {
                console.log('Block L render check - blockLData:', blockLData);
                console.log('Block L render check - blockLData.length:', blockLData.length);
                console.log('Block L render check - blockLItemData:', blockLItemData);
                
                return blockLData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th style={{ width: '10%' }}>{tableHeaders.slNo}</th>
                          <th style={{ width: '70%' }}>{tableHeaders.ecIndicator}</th>
                          <th style={{ width: '20%' }}>{tableHeaders.yesNo}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blockLData.map((item, index) => {
                          console.log('Rendering Block L item:', item);
                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td className="item-name">{item.ItemsName}</td>
                              <td>{blockLItemData[item.ecIndicatorCode]?.code || ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="no-data">No Block L data available</div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockMAndNreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockKAndLReport;